<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { PhSignIn, PhBookmarksSimple, PhUser } from '@phosphor-icons/vue'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useGoogleAuth } from '@/composables/useGoogleAuth'

const router = useRouter()
const logoutConfirmOpen = ref(false)
const { isSignedIn, profile, signIn, signOut } = useGoogleAuth()

const initials = computed(() => {
  const raw = (profile.value.name || profile.value.email || '?').trim()
  const parts = raw.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0]![0] + parts[1]![0]).toUpperCase()
  }
  return raw.slice(0, 2).toUpperCase()
})

const triggerBtnClass =
  'h-10 shrink-0 rounded-full border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted'

const iconTriggerClass =
  'h-10 w-10 shrink-0 rounded-full border border-border text-foreground hover:bg-muted'

function openLogoutConfirm() {
  logoutConfirmOpen.value = true
}

async function confirmLogout() {
  await signOut()
  logoutConfirmOpen.value = false
  void router.push('/quran')
}
</script>

<template>
  <div class="flex items-center gap-2">
    <template v-if="isSignedIn">
      <DropdownMenu>
        <DropdownMenuTrigger class="outline-none">
          <Button type="button" variant="ghost" :class="[iconTriggerClass, 'p-0.5']" aria-label="Account menu">
            <img
              v-if="profile.picture"
              :src="profile.picture"
              alt=""
              class="h-9 w-9 rounded-full object-cover"
              width="36"
              height="36"
              referrerpolicy="no-referrer"
            />
            <span
              v-else
              class="flex h-9 w-9 items-center justify-center rounded-full bg-moss-700/90 text-xs font-semibold text-bullion-50"
            >
              {{ initials }}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="min-w-[13rem]" align="end" :side-offset="8">
          <DropdownMenuLabel class="font-normal">
            <p class="truncate text-sm font-semibold text-foreground">{{ profile.name || 'Signed in' }}</p>
            <p v-if="profile.email" class="truncate text-xs text-muted-foreground">{{ profile.email }}</p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem as-child class="cursor-pointer">
            <RouterLink to="/profile" class="flex w-full cursor-pointer items-center gap-2">
              <PhUser :size="18" class="text-muted-foreground" aria-hidden="true" />
              Profile
            </RouterLink>
          </DropdownMenuItem>
          <DropdownMenuItem as-child class="cursor-pointer">
            <RouterLink to="/bookmarks" class="flex w-full cursor-pointer items-center gap-2">
              <PhBookmarksSimple :size="18" class="text-muted-foreground" aria-hidden="true" />
              Bookmarks
            </RouterLink>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            class="cursor-pointer text-destructive focus:text-destructive"
            @click="openLogoutConfirm"
          >
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog v-model:open="logoutConfirmOpen">
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Log out?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be signed out of this browser session. You can sign in again any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              class="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              @click="confirmLogout"
            >
              Log out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </template>

    <template v-else>
      <Button type="button" variant="outline" :class="[triggerBtnClass, 'hidden sm:inline-flex']" @click="signIn">
        Log in
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        :class="[iconTriggerClass, 'sm:hidden']"
        aria-label="Log in"
        @click="signIn"
      >
        <PhSignIn :size="20" />
      </Button>
    </template>
  </div>
</template>
